import { createContext, useContext, type CSSProperties, type MouseEvent, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon, landingVars, type LandingPalette } from '@/design-system';
import { cx } from '@/lib/cx';
import { formatSum } from '@/lib/format';
import { DEVICE_WIDTH, type Device } from './bridge';
import s from './designer.module.css';

export type Selection = { section: string | null; el: string | null };

type Pick = { pick: boolean; selection: Selection; onSelect: (sel: Selection) => void };
const PickCtx = createContext<Pick>({ pick: false, selection: { section: null, el: null }, onSelect: () => undefined });

/** A section: a click selects it; a click on a marked element inside the selected section selects that element (C3). */
function Sec({ id, className, children }: { id: string; className?: string; children: ReactNode }) {
  const { t } = useTranslation('designer');
  const { pick, selection, onSelect } = useContext(PickCtx);
  const picked = pick && selection.section === id;
  const click = (e: MouseEvent) => {
    if (!pick) return;
    const elId = (e.target as HTMLElement).closest<HTMLElement>('[data-el]')?.dataset.el ?? null;
    onSelect(picked && elId ? { section: id, el: elId } : { section: id, el: null });
  };
  return (
    <div className={cx(s.sec, className)} data-section={id} data-picked={picked} onClick={click}>
      {children}
      {picked && <span className={s.tag}>{selection.el ? t('preview.tag_el', { id, el: selection.el }) : t('preview.tag', { id })}</span>}
    </div>
  );
}

/** An element the PM can select inside the selected section (the builder's `data-el` ids). */
function El({ id, as: Tag = 'span', className, children }: { id: string; as?: 'span' | 'h2' | 'p' | 'div'; className?: string; children?: ReactNode }) {
  const { pick, selection } = useContext(PickCtx);
  return (
    <Tag className={cx(s.el, className)} data-el={id} data-picked={pick && selection.el === id}>
      {children}
    </Tag>
  );
}

type Props = Pick & {
  palette: LandingPalette | null | undefined;
  brand: string;
  headline: string;
  products: { name: string; price: number | null }[];
  device: Device;
  label: string;
};

/**
 * The variant's landing as the design draws it — the stand-in `LandingPreview` until the variant's
 * preview build is served from the preview origin: the variant palette, Instrument Serif headings, the
 * nav, the hero, «Категории» and «Популярное» (approved products only). With «Выделить» on, a click
 * selects a section (2 px orange outline, the mono tag `section.hero`); inside the selected section a
 * click selects an element (C3, PLAN D21), in the same orange style.
 */
export function StandInPreview({ palette, brand, headline, products, device, pick, selection, onSelect, label }: Props) {
  const { t } = useTranslation('designer');
  const mobile = device === 'mobile';
  const width = DEVICE_WIDTH[device];
  const nav = t('stand_in.nav', { returnObjects: true }) as string[];
  const cats = t('stand_in.category_names', { returnObjects: true }) as string[];
  const style = { ...landingVars(palette), width: width ? `${width}px` : '100%', '--pv-pad': mobile ? '20px' : '40px' } as CSSProperties;
  return (
    <PickCtx.Provider value={{ pick, selection, onSelect }}>
      <div className={s.page} style={style} data-device={device} data-pick={pick} role="region" aria-label={label}>
        <Sec id="header" className={s.nav}>
          <El id="logo" className={s.brand}>
            {brand}
          </El>
          {!mobile && (
            <div className={s.links}>
              {nav.map((n) => (
                <span key={n}>{n}</span>
              ))}
            </div>
          )}
          <div className={s.icons}>
            <Icon name="search" size={17} title={t('stand_in.search')} />
            <Icon name="shopping-bag" size={17} title={t('stand_in.cart')} />
          </div>
        </Sec>
        <Sec id="hero" className={s.hero}>
          <div className={s.heroCopy}>
            <El id="eyebrow" className={s.eyebrow}>
              {t('stand_in.eyebrow')}
            </El>
            <El id="title" as="h2" className={s.h1}>
              {headline}
            </El>
            <El id="lead" as="p" className={s.lead}>
              {t('stand_in.sub')}
            </El>
            <div className={s.ctas}>
              <El id="cta.0" className={s.cta}>
                {t('stand_in.cta')}
              </El>
              <El id="cta.1" className={s.cta2}>
                {t('stand_in.cta2')}
              </El>
            </div>
          </div>
          <El id="image" as="div" className={s.heroImage}>
            <span className={s.caption}>{t('stand_in.image')}</span>
          </El>
        </Sec>
        <Sec id="categories" className={s.block}>
          <El id="heading" className={s.blockTitle}>
            {t('stand_in.categories')}
          </El>
          <div className={s.tiles}>
            {cats.map((c, i) => (
              <El key={c} id={`tile.${i}`} as="div" className={s.tile}>
                {c}
              </El>
            ))}
          </div>
        </Sec>
        <Sec id="featured" className={s.blockLast}>
          <div className={s.blockHead}>
            <El id="heading" className={s.blockTitle}>
              {t('stand_in.popular')}
            </El>
            <span className={s.more}>{t('stand_in.all')}</span>
          </div>
          <div className={s.products}>
            {products.map((p, i) => (
              <El key={`${p.name}-${i}`} id={`card.${i}`} as="div" className={s.product}>
                <div className={s.productImage} />
                <span className={s.productName}>{p.name}</span>
                <span className={s.productPrice}>{p.price === null ? '' : t('stand_in.price', { price: formatSum(p.price) })}</span>
              </El>
            ))}
          </div>
        </Sec>
      </div>
    </PickCtx.Provider>
  );
}
